// 即时风险订单列表（横向卡片，与 Figma「餐厅风控-即时风险订单列表」一致）
import React from 'react';
import { Box, Typography } from '@mui/material';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import { GetThemeMode } from '../../themes/tool';
import { COLOR_KEY, getModeColor } from '../../themes/customization.value';
import i18n from '../../i18n';
import { formatEatTypeText, formatEventTypeText, formatRiskMinutes, formatRiskMoney, formatRiskStatusText, getRiskStatusColor } from '../../constants/tool-order';
import { RiskEvent, RiskOrder } from '../../models';
import { dateFormat } from '../../constants/utils';
import { ReactComponent as RiskWarningTriangleIcon } from '../../static/images/svg/risk-order-row-warning.svg';
import { getRiskTagTextForOrder, getRiskTagsForOrder, RiskOrderGroup } from './risk-order-mode';

export interface RiskOrderSwiperProps {
    orders: Array<RiskOrder | RiskEvent>;
    onOrderClick?: (order: RiskOrder) => void;
    onEventClick?: (event: RiskEvent) => void;
}

/** 卡片内相邻行之间的分隔与间距（设计稿约 8px） */
const ROW_STACK_SX = {
    flex: 1,
    '& > * + *': {
        borderTop: '1px solid var(--stroke-neutral-800-a25)',
        mt: 2,
        pt: 2,
    },
} as const;

/**
 * 普通信息行：主题色圆点 + 深色标签 + 加粗数值（可选数值颜色，如状态）
 * @param label - 行标题
 * @param value - 行展示值
 * @param valueColor - 数值颜色（如风控状态语义色），缺省为深色字体
 * @param wrapValue - 是否限制右侧值宽度并允许风险标签换行
 * @param warning - 是否为异常指标行，显示警告图标且左右文字使用错误色
 */
export const InfoRow: React.FC<{
    label: string;
    value?: React.ReactNode;
    valueColor?: string;
    /** 是否限制右侧值宽度并允许风险标签换行 */
    wrapValue?: boolean;
    /** 是否为异常指标行 */
    warning?: boolean;
}> = ({
    label,
    value,
    valueColor,
    wrapValue = false,
    warning = false,
}) => {
    const themeMode = GetThemeMode();
    const getColor = (key: COLOR_KEY) => getModeColor(key, themeMode);
    const textDark = getColor(COLOR_KEY.iconNeutral800) as string;
    const errorColor = getColor(COLOR_KEY.funcError) as string;
    const valueTone = warning ? errorColor : valueColor ?? textDark;
    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: wrapValue || warning ? 'flex-start' : 'center',
                gap: 1,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: '1 1 auto', minWidth: 0 }}>
                {warning ? (
                    <Box
                        sx={{
                            width: 24,
                            height: 24,
                            flexShrink: 0,
                            color: errorColor,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                        aria-hidden
                    >
                        <RiskWarningTriangleIcon width={24} height={24} />
                    </Box>
                ) : (
                    <Box
                        sx={{
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            bgcolor: 'var(--text-primary-300)',
                            flexShrink: 0,
                        }}
                    />
                )}
                <Typography variant="body2" sx={{ color: warning ? errorColor : textDark, minWidth: 0 }}>
                    {label}
                </Typography>
            </Box>
            <Typography
                variant="subtitle2"
                sx={{
                    color: valueTone,
                    textAlign: 'right',
                    minWidth: 0,
                    ...(wrapValue && {
                        flex: '0 1 auto',
                        maxWidth: '55%',
                        whiteSpace: 'normal',
                        overflowWrap: 'anywhere',
                    }),
                }}
            >
                {value ?? '-'}
            </Typography>
        </Box>
    );
};

const formatDateTime = (val?: string) => {
    if (!val) return '-';
    return dateFormat(val, 'HH:mm DD/MM/YYYY');
};

const isRiskOrder = (item: RiskOrder | RiskEvent): item is RiskOrder => {
    return (item as RiskOrder).order_head_id !== undefined;
};

export const RiskOrderSwiper: React.FC<RiskOrderSwiperProps> = ({ orders, onOrderClick, onEventClick }) => {
    const themeMode = GetThemeMode();
    const getColor = (key: COLOR_KEY) => getModeColor(key, themeMode);

    return (
        <Box sx={{ width: '100%' }} className="risk-order-swiper">
            <Swiper spaceBetween={24} slidesPerView={'auto'} style={{ padding: '8px 0 16px' }}>
                {orders.map((order, index) => {
                    if (isRiskOrder(order)) {
                        const statusColor = getRiskStatusColor(order.orderStatus ?? order.risk_status ?? 0);
                        const riskOrder = order as RiskOrderGroup;
                        const riskTags = getRiskTagsForOrder(riskOrder);
                        const hasRiskType = (riskType: number) =>
                            riskTags.some((tag) => tag.type === 'risk' && tag.value === riskType);
                        const riskHighlightColor = getColor(COLOR_KEY.funcError) as string;

                        return (
                            <SwiperSlide
                                key={`${order.order_head_id}${index}`}
                                style={{ width: 360, maxWidth: '80vw', display: 'flex', height: 'unset' }}
                            >
                                <Box
                                    sx={{
                                        borderRadius: '16px',
                                        boxShadow: '0 0 4px 0 var(--shadow-25)',
                                        padding: '16px',
                                        marginLeft: '2px',
                                        cursor: 'pointer',
                                        background: getColor(COLOR_KEY.iconNeutral100),
                                        border: '4px solid var(--icon-neutral-100)',
                                        '&:hover': {
                                            border: '4px solid var(--text-primary-300)',
                                        },
                                        width: '100%',
                                        height: '100%',
                                        boxSizing: 'border-box',
                                    }}
                                    onClick={() => onOrderClick?.(order)}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            mb: 1,
                                        }}
                                    >
                                        <Typography variant="h4" sx={{ color: getColor(COLOR_KEY.funcError) }}>
                                            {order.card_title || i18n.t('intelligent_risk_control_page_text_abnormal_orders')}
                                        </Typography>
                                    </Box>

                                    <Box sx={ROW_STACK_SX}>
                                        <InfoRow label={i18n.t('business_analysis_page_order_account')} value={order.order_head_id} />
                                        <InfoRow
                                            label={i18n.t('intelligent_risk_control_page_column_amount_original_actual')}
                                            value={`${formatRiskMoney(order.origin_amount)}/${formatRiskMoney(order.actual_amount)}`}
                                        />
                                        <InfoRow label={i18n.t('business_analysis_page_table_name')} value={order.table_name} />
                                        <InfoRow label={i18n.t('immediate_number_people')} value={order.people_count} />
                                        <InfoRow label={i18n.t('intelligent_risk_control_page_column_dish_count')} value={order.dish_count} />
                                        <InfoRow label={i18n.t('intelligent_risk_control_page_column_server')} value={order.operator_name} />
                                        <InfoRow label={i18n.t('immediate_table_opening_time')} value={formatDateTime(order.open_time)} />
                                        <InfoRow label={i18n.t('immediate_statement_time')} value={formatDateTime(order.close_time)} />
                                        <InfoRow
                                            label={i18n.t('intelligent_risk_control_page_column_discount_amount')}
                                            value={formatRiskMoney(order.discount_amount)}
                                            warning={hasRiskType(1) && Boolean(order.discount_amount)}
                                        />
                                        <InfoRow
                                            label={i18n.t('intelligent_risk_control_page_column_refund_count')}
                                            value={order.refund_dish_count}
                                            warning={hasRiskType(2)}
                                        />
                                        <InfoRow
                                            label={i18n.t('intelligent_risk_control_page_column_refund_amount')}
                                            value={formatRiskMoney(order.refund_amount)}
                                            warning={hasRiskType(3) && Boolean(order.refund_amount)}
                                        />
                                        <InfoRow
                                            label={i18n.t('intelligent_risk_control_page_column_avg_dish_count')}
                                            value={order.avg_dish_per_person}
                                            warning={hasRiskType(4)}
                                        />
                                        <InfoRow
                                            label={i18n.t('intelligent_risk_control_page_column_duration_minutes')}
                                            value={formatRiskMinutes(order.dining_duration_min)}
                                            warning={hasRiskType(5)}
                                        />
                                        <InfoRow
                                            label={i18n.t('intelligent_risk_control_page_filter_risk_tag')}
                                            value={getRiskTagTextForOrder(riskOrder)}
                                            valueColor={riskHighlightColor}
                                            wrapValue
                                        />
                                        <InfoRow
                                            label={i18n.t('restaurant_info_page_status')}
                                            value={i18n.t(formatRiskStatusText(order.orderStatus ?? order.risk_status))}
                                            valueColor={statusColor}
                                        />
                                    </Box>
                                </Box>
                            </SwiperSlide>
                        );
                    }

                    const event = order as RiskEvent;
                    const eventColor = getRiskStatusColor(event?.orders?.[0]?.event_status ?? 0);

                    return (
                        <SwiperSlide
                            key={event.eventId ?? `event-${index}`}
                            style={{ width: 360, maxWidth: '80vw', display: 'flex', height: 'unset' }}
                        >
                            <Box
                                sx={{
                                    borderRadius: '16px',
                                    boxShadow: '0 0 4px 0 var(--shadow-25)',
                                    padding: '16px',
                                    marginLeft: '2px',
                                    cursor: 'pointer',
                                    background: getColor(COLOR_KEY.iconNeutral100),
                                    border: '4px solid var(--icon-neutral-100)',
                                    '&:hover': {
                                        border: '4px solid var(--text-primary-300)',
                                    },
                                    width: '100%',
                                    height: '100%',
                                    boxSizing: 'border-box',
                                }}
                                onClick={() => onEventClick?.(event)}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        mb: 1,
                                    }}
                                >
                                    <Typography variant="h4" sx={{ color: getColor(COLOR_KEY.funcError) }}>
                                        {i18n.t('intelligent_risk_control_page_text_abnormal_events') +
                                            '（' +
                                            i18n.t(formatEventTypeText(event.eventType)) +
                                            '）'}
                                    </Typography>
                                </Box>

                                <Box sx={ROW_STACK_SX}>
                                    {event.orders?.map((o, idx) => (
                                        <InfoRow
                                            key={`${o.order_head_id}${idx}`}
                                            label={i18n.t('business_analysis_page_order_account')}
                                            value={o.order_head_id}
                                        />
                                    ))}
                                    <InfoRow
                                        label={i18n.t('immediate_order_type')}
                                        value={event.eatType === undefined ? '-' : formatEatTypeText(event.eatType) || '-'}
                                    />
                                    <InfoRow
                                        label={i18n.t('intelligent_risk_control_page_filter_risk_tag')}
                                        value={i18n.t(formatEventTypeText(event.eventType))}
                                        valueColor={getColor(COLOR_KEY.funcError) as string}
                                    />
                                    <InfoRow
                                        label={i18n.t('restaurant_extensions_page_text_create_time')}
                                        value={formatDateTime(event.createdAt)}
                                    />
                                    <InfoRow
                                        label={i18n.t('restaurant_info_page_status')}
                                        value={i18n.t(formatRiskStatusText(event.orders?.[0]?.event_status ?? 0))}
                                        valueColor={eventColor}
                                    />
                                </Box>
                            </Box>
                        </SwiperSlide>
                    );
                })}
            </Swiper>
        </Box>
    );
};

export default RiskOrderSwiper;
